import type { ReactNode } from "react";
import { TextField, btnGhost, textInput } from "@/pages/admin/components/AdminUI";

export const CARD = "bg-background-50 border border-background-200 rounded-lg p-5 md:p-6";

export function SectionCard({
  icon,
  title,
  subtitle,
  children,
}: {
  icon: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
}) {
  return (
    <section className={CARD}>
      <div className="flex items-center gap-3 mb-5">
        <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className={icon}></i>
        </span>
        <div>
          <h2 className="font-heading text-xl text-foreground-950 font-semibold">{title}</h2>
          {subtitle ? <p className="text-xs text-foreground-500">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </section>
  );
}

export function RowCard({
  title,
  onRemove,
  children,
}: {
  title?: string;
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <div className="border border-background-200 rounded-md p-4">
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-3">
          {title ? (
            <p className="text-[11px] font-semibold uppercase tracking-wider text-foreground-500">
              {title}
            </p>
          ) : null}
          {children}
        </div>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove"
          className="w-9 h-9 flex items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100 cursor-pointer shrink-0"
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>
    </div>
  );
}

export function AddRowButton({ onClick, label }: { onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} className={`${btnGhost} mt-3`}>
      <i className="ri-add-line"></i> {label}
    </button>
  );
}

export function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  className,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}) {
  return (
    <TextField label={label}>
      <input
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        className={`${textInput} ${className || ""}`}
      />
    </TextField>
  );
}

export function LabeledTextarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <TextField label={label}>
      <textarea
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={`${textInput} resize-none`}
      ></textarea>
    </TextField>
  );
}

export function LabeledSelect({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <TextField label={label}>
      <select
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value)}
        className={`${textInput} cursor-pointer`}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </TextField>
  );
}